import { useMemo } from 'react'
import { Amount, type IDFor, LowercaseMap, getNativeAddress } from 'sushi'
import type { EvmChainId } from 'sushi/evm'
import type { UseBalancesReturn } from './types'
import { useEvmBalances } from './use-evm-balances'

export function useBalances<TChainId extends EvmChainId>(
  chainId: TChainId | undefined,
  tokenAddresses: AddressFor<TChainId>[] | undefined,
): UseBalancesReturn<TChainId> {
  const evmBalances = useEvmBalances(chainId, tokenAddresses)

  if (!chainId) {
    return {
      data: undefined,
      isError: false,
      isLoading: false,
      isFetching: false,
    }
  }

  return evmBalances as UseBalancesReturn<TChainId>
}

export function useAmountBalances<TChainId extends EvmChainId>(
  chainId: TChainId | undefined,
  _currencies: (CurrencyFor<TChainId> | undefined)[] | undefined,
) {
  const currencies = useMemo(() => {
    if (!_currencies) {
      return undefined
    }

    return _currencies.filter(
      (currency) => currency !== undefined,
    ) as CurrencyFor<TChainId>[]
  }, [_currencies])

  const tokenAddresses = useMemo(() => {
    if (!currencies) {
      return undefined
    }

    return currencies.map((currency) => {
      if (currency.chainId !== chainId) {
        throw new Error(
          'useAmountBalances: All currencies must be on the same chain',
        )
      }

      if (currency.type === 'native') {
        return getNativeAddress(chainId)
      }

      return currency.address as AddressFor<TChainId>
    })
  }, [chainId, currencies])

  const result = useBalances(chainId, tokenAddresses)

  return useMemo(() => {
    if (!chainId || !currencies || result.data === undefined) {
      return {
        ...result,
        data: undefined,
      }
    }

    const nativeAddress = getNativeAddress(chainId)
    const amountMap: Map<
      IDFor<TChainId, true>,
      Amount<CurrencyFor<TChainId>>
    > = new LowercaseMap()

    currencies.forEach((currency) => {
      const address =
        currency.type === 'native'
          ? nativeAddress
          : (currency.address as AddressFor<TChainId>)
      const amount = result.data.get(address)

      if (amount === undefined) {
        return
      }

      amountMap.set(
        currency.id as IDFor<TChainId, true>,
        new Amount(currency, amount),
      )
    })

    return {
      ...result,
      data: amountMap as ReadonlyMap<
        IDFor<TChainId, true>,
        Amount<CurrencyFor<TChainId>>
      >,
    }
  }, [currencies, result, chainId])
}
